<template>
  <transition name="cp-link-fade">
    <base-modal v-if="visible" fixed-footer :fullscreen="maximized" width="720px" height="85vh" @close="handleClose">
      <template #headerActions>
        <button type="button" class="base-modal-control-btn base-modal-control-btn--expand cp-link-modal-expand-btn"
          :aria-label="maximized ? 'Exit full screen' : 'Full screen'" @click="maximized = !maximized">
          <font-awesome-icon :icon="['fas', maximized ? 'compress' : 'expand']" />
        </button>
      </template>

      <template #header>
        <div class="cp-link-modal-header" :class="{ 'cp-link-modal-header--fullscreen': maximized }">
          <div class="cp-link-modal-header-inner">
            <div class="cp-link-modal-header-icon-wrap">
              <font-awesome-icon :icon="step === 'linking' ? ['fas', 'spinner'] : ['fas', 'link']"
                :spin="step === 'linking'" class="cp-link-modal-header-icon" />
            </div>
            <div class="text-left min-w-0">
              <h2 class="cp-link-modal-header-title">{{ headerTitle }}</h2>
              <p class="cp-link-modal-header-sub">{{ headerSub }}</p>
            </div>
          </div>
        </div>
      </template>

      <div class="cp-link-modal-shell">
        <div class="cp-link-modal-scroll">
          <template v-if="step === 'select'">
            <div v-if="isLoading" class="cp-link-modal-loading">
              <base-page-spinner type="spin" class-list="h-16"></base-page-spinner>
              <p>Loading employer links…</p>
            </div>

            <div v-else-if="loadError" class="cp-link-modal-card cp-link-modal-card--error">
              <p class="cp-link-modal-card__title cp-link-modal-card__title--error">{{ loadError }}</p>
            </div>

            <template v-else>
              <div class="cp-link-tabs">
                <button v-for="tab in modalTabs" :key="tab.value" type="button" class="cp-link-tab"
                  :class="{ 'cp-link-tab--active': activeTab === tab.value }" @click="activeTab = tab.value">
                  <font-awesome-icon :icon="tab.icon" class="cp-link-tab__icon" />
                  <span class="cp-link-tab__label">{{ tab.label }}</span>
                  <span v-if="tab.badge" class="cp-link-tab__badge">{{ tab.badge }}</span>
                </button>
              </div>

              <div v-if="activeTab === 'linked'" class="cp-link-tab-panel">
                <div class="cp-link-linked-hero">
                  <div class="cp-link-linked-hero__copy">
                    <h3 class="cp-link-linked-hero__title">Employers linked to {{ partnerName }}</h3>
                    <p class="cp-link-linked-hero__sub">
                      Employer names currently associated with this partner and their student counts.
                    </p>
                  </div>
                  <div class="cp-link-linked-hero__stats">
                    <div class="cp-link-linked-stat">
                      <p class="cp-link-linked-stat__value">{{ linkedEmployers.length }}</p>
                      <p class="cp-link-linked-stat__label">Employers</p>
                    </div>
                    <div class="cp-link-linked-stat">
                      <p class="cp-link-linked-stat__value">{{ totalLinkedStudents }}</p>
                      <p class="cp-link-linked-stat__label">Students</p>
                    </div>
                  </div>
                </div>

                <div v-if="linkedEmployers.length" class="cp-link-linked-table-wrap">
                  <div class="cp-link-linked-table-toolbar">
                    <button type="button" class="cp-link-modal-text-btn" @click="toggleAllLinked">
                      {{ allLinkedSelected ? "Deselect all" : "Select all" }}
                    </button>
                    <p v-if="selectedUnlinkCount > 0" class="cp-link-linked-table-toolbar__hint">
                      {{ selectedUnlinkCount }} selected to unlink
                    </p>
                  </div>
                  <div class="cp-link-linked-table-head">
                    <span class="cp-link-linked-table-head__col cp-link-linked-table-head__col--check"></span>
                    <span class="cp-link-linked-table-head__col cp-link-linked-table-head__col--name">Employer
                      name</span>
                    <span class="cp-link-linked-table-head__col cp-link-linked-table-head__col--count">Students</span>
                    <span class="cp-link-linked-table-head__col cp-link-linked-table-head__col--share">Share</span>
                  </div>
                  <div class="cp-link-linked-table-body">
                    <label v-for="(employer, index) in linkedEmployers" :key="'linked-row-' + employer.employer"
                      class="cp-link-linked-row"
                      :class="{ 'cp-link-linked-row--selected': isUnlinkSelected(employer.employer) }">
                      <span class="cp-link-linked-row__check">
                        <input type="checkbox" class="cp-link-employer-item__checkbox"
                          :checked="isUnlinkSelected(employer.employer)"
                          @change="toggleUnlinkSelection(employer.employer)" />
                      </span>
                      <div class="cp-link-linked-row__main">
                        <span class="cp-link-linked-row__rank">{{ index + 1 }}</span>
                        <div class="cp-link-linked-row__info">
                          <p class="cp-link-linked-row__name">{{ employer.employer }}</p>
                          <div class="cp-link-linked-row__bar-wrap">
                            <div class="cp-link-linked-row__bar" :style="{ width: linkedShareWidth(employer.count) }">
                            </div>
                          </div>
                        </div>
                      </div>
                      <span class="cp-link-linked-row__count">{{ employer.count }}</span>
                      <span class="cp-link-linked-row__share">{{ linkedShareLabel(employer.count) }}</span>
                    </label>
                  </div>
                </div>

                <div v-else class="cp-link-linked-empty">
                  <div class="cp-link-linked-empty__icon-wrap">
                    <font-awesome-icon :icon="['fas', 'link']" />
                  </div>
                  <p class="cp-link-linked-empty__title">No employers linked yet</p>
                  <p class="cp-link-linked-empty__text">
                    Switch to the Link Employers tab to associate employer names with {{ partnerName }}.
                  </p>
                  <button type="button" class="cp-link-linked-empty__btn" @click="activeTab = 'link'">
                    <font-awesome-icon :icon="['fas', 'plus-circle']" />
                    <span>Link Employers</span>
                  </button>
                </div>
              </div>

              <div v-else class="cp-link-tab-panel">
                <div class="cp-link-modal-ready">
                  <font-awesome-icon :icon="['fas', 'info-circle']" class="cp-link-modal-ready__icon" />
                  <div>
                    <p class="cp-link-modal-ready__title">Link employers to {{ partnerName }}</p>
                    <p class="cp-link-modal-ready__sub">
                      Matching names are pre-selected.
                    </p>
                  </div>
                </div>

                <div class="cp-link-modal-card">
                  <div class="cp-link-modal-card__head cp-link-modal-card__head--row">
                    <div>
                      <h3 class="cp-link-modal-card__title">Matching employers</h3>
                      <p class="cp-link-modal-card__sub">
                        Unlinked employers that contain “{{ partnerName }}”
                      </p>
                    </div>
                    <button v-if="matchedEmployers.length" type="button" class="cp-link-modal-text-btn"
                      @click="toggleAllMatched">
                      {{ allMatchedSelected ? "Deselect all" : "Select all" }}
                    </button>
                  </div>

                  <div v-if="matchedEmployers.length" class="cp-link-employer-list">
                    <label v-for="employer in matchedEmployers" :key="'matched-' + employer.name"
                      class="cp-link-employer-item cp-link-employer-item--matched">
                      <input type="checkbox" class="cp-link-employer-item__checkbox"
                        :checked="isSelected(employer.name)" @change="toggleSelection(employer.name)" />
                      <span class="cp-link-employer-item__body">
                        <span class="cp-link-employer-item__name">{{ employer.name }}</span>
                        <span class="cp-link-employer-item__meta">
                          {{ employer.student_count }} student record{{ employer.student_count === 1 ? "" : "s" }}
                        </span>
                      </span>
                      <span class="cp-link-employer-item__badge">Match</span>
                    </label>
                  </div>
                  <div v-else class="cp-link-empty-inline">
                    <font-awesome-icon :icon="['fas', 'search']" />
                    <p>No matching unlinked employers found for this partner name.</p>
                  </div>
                </div>

                <div class="cp-link-modal-card">
                  <div class="cp-link-modal-card__head">
                    <h3 class="cp-link-modal-card__title">Other unlinked employers</h3>
                    <p class="cp-link-modal-card__sub">
                      Search and select additional employer names to link manually
                    </p>
                  </div>

                  <div class="cp-link-search-wrap">
                    <font-awesome-icon :icon="['fas', 'search']" class="cp-link-search-wrap__icon" />
                    <input v-model="searchInput" type="text" class="cp-link-search-wrap__input"
                      placeholder="Search employer name…" @input="onSearchInput" />
                  </div>

                  <div v-if="othersLoading" class="cp-link-modal-loading cp-link-modal-loading--compact">
                    <base-page-spinner type="spin" class-list="h-10"></base-page-spinner>
                  </div>

                  <div v-else-if="otherEmployers.length" class="cp-link-employer-list">
                    <label v-for="employer in otherEmployers" :key="'other-' + employer.name"
                      class="cp-link-employer-item">
                      <input type="checkbox" class="cp-link-employer-item__checkbox"
                        :checked="isSelected(employer.name)" @change="toggleSelection(employer.name)" />
                      <span class="cp-link-employer-item__body">
                        <span class="cp-link-employer-item__name">{{ employer.name }}</span>
                        <span class="cp-link-employer-item__meta">
                          {{ employer.student_count }} student record{{ employer.student_count === 1 ? "" : "s" }}
                        </span>
                      </span>
                    </label>
                  </div>
                  <div v-else class="cp-link-empty-inline">
                    <font-awesome-icon :icon="['fas', 'building']" />
                    <p>{{ searchInput ? "No employers match your search." : "No other unlinked employers found." }}</p>
                  </div>

                  <div v-if="paginatorInfo && paginatorInfo.lastPage > 1" class="cp-link-pagination">
                    <button type="button" class="cp-link-pagination__btn"
                      :disabled="othersLoading || paginatorInfo.currentPage <= 1"
                      @click="goToPage(paginatorInfo.currentPage - 1)">
                      Previous
                    </button>
                    <span class="cp-link-pagination__label">
                      Page {{ paginatorInfo.currentPage }} of {{ paginatorInfo.lastPage }}
                    </span>
                    <button type="button" class="cp-link-pagination__btn"
                      :disabled="othersLoading || !paginatorInfo.hasMorePages"
                      @click="goToPage(paginatorInfo.currentPage + 1)">
                      Next
                    </button>
                  </div>
                </div>

                <p class="cp-link-modal-hint">
                  <font-awesome-icon :icon="['fas', 'lightbulb']" class="cp-link-modal-hint-icon" />
                  {{ selectedCount }} employer{{ selectedCount === 1 ? "" : "s" }} selected to link.
                </p>
              </div>
            </template>
          </template>

          <template v-else-if="step === 'done'">
            <div class="cp-link-modal-summary">
              <div class="cp-link-modal-summary__stat cp-link-modal-summary__stat--ok">
                <p class="cp-link-modal-summary__value">{{ linkedCount }}</p>
                <p class="cp-link-modal-summary__label">Linked</p>
              </div>
            </div>
            <div class="cp-link-modal-ready">
              <font-awesome-icon :icon="['fas', 'check-circle']" class="cp-link-modal-ready__icon" />
              <div>
                <p class="cp-link-modal-ready__title">{{ successMessage }}</p>
                <p class="cp-link-modal-ready__sub">Employment insights will refresh with the newly linked records.</p>
              </div>
            </div>
          </template>
        </div>

        <div class="cp-link-modal-footer" :class="{ 'cp-link-modal-footer--fullscreen': maximized }">
          <div class="cp-link-modal-actions">
            <template v-if="step === 'select'">
              <button type="button" class="cp-link-modal-btn cp-link-modal-btn-secondary" @click="handleClose">
                {{ activeTab === 'linked' ? 'Close' : 'Cancel' }}
              </button>
              <button v-if="activeTab === 'link'" type="button" class="cp-link-modal-btn cp-link-modal-btn-primary"
                :disabled="isLoading || isLinking || selectedCount === 0" @click="confirmLink">
                <font-awesome-icon :icon="['fas', 'link']" class="mr-2" />
                Link {{ selectedCount }} employer{{ selectedCount === 1 ? "" : "s" }}
              </button>
              <button v-if="activeTab === 'linked'" type="button" class="cp-link-modal-btn cp-link-modal-btn-danger"
                :disabled="isUnlinking || selectedUnlinkCount === 0" @click="confirmUnlink">
                <font-awesome-icon :icon="['fas', 'unlink']" class="mr-2" />
                Unlink {{ selectedUnlinkCount }} employer{{ selectedUnlinkCount === 1 ? "" : "s" }}
              </button>
            </template>
            <template v-else-if="step === 'done'">
              <button type="button" class="cp-link-modal-btn cp-link-modal-btn-primary" @click="handleClose">
                Done
              </button>
            </template>
          </div>
        </div>
      </div>
    </base-modal>
  </transition>
</template>

<script>
import gql from "graphql-tag";

export default {
  name: "LinkEmployersModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    partnerToken: {
      type: String,
      required: true,
    },
    partnerName: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      maximized: false,
      step: "select",
      isLoading: false,
      othersLoading: false,
      isLinking: false,
      isUnlinking: false,
      loadError: null,
      searchInput: "",
      searchDebounceTimer: null,
      currentPage: 1,
      perPage: 10,
      matchedEmployers: [],
      linkedEmployers: [],
      activeTab: "link",
      modalTabs: [
        { value: "link", label: "Link Employers", icon: ["fas", "plus-circle"], badge: "" },
        { value: "linked", label: "Linked Employers", icon: ["fas", "check-circle"], badge: "" },
      ],
      otherEmployers: [],
      paginatorInfo: null,
      selectedNames: {},
      selectedUnlinkNames: {},
      linkedCount: 0,
      successMessage: "",
    };
  },
  computed: {
    headerTitle() {
      if (this.step === "done") {
        return "Employers linked";
      }
      return "Employer links";
    },
    headerSub() {
      if (this.step === "done") {
        return "Records have been associated with " + (this.partnerName || "this partner");
      }
      if (this.linkedEmployers.length > 0) {
        return (
          this.linkedEmployers.length +
          " employer" +
          (this.linkedEmployers.length === 1 ? "" : "s") +
          " linked · " +
          this.totalLinkedStudents +
          " student" +
          (this.totalLinkedStudents === 1 ? "" : "s")
        );
      }
      return "View linked employers or associate new names with " + (this.partnerName || "this partner");
    },
    totalLinkedStudents() {
      var total = 0;
      for (var i = 0; i < this.linkedEmployers.length; i++) {
        total += Number(this.linkedEmployers[i].count) || 0;
      }
      return total;
    },
    selectedCount() {
      var count = 0;
      var keys = Object.keys(this.selectedNames);
      for (var i = 0; i < keys.length; i++) {
        if (this.selectedNames[keys[i]]) {
          count++;
        }
      }
      return count;
    },
    allMatchedSelected() {
      if (!this.matchedEmployers.length) {
        return false;
      }
      for (var i = 0; i < this.matchedEmployers.length; i++) {
        if (!this.selectedNames[this.matchedEmployers[i].name]) {
          return false;
        }
      }
      return true;
    },
    selectedUnlinkCount() {
      var count = 0;
      var keys = Object.keys(this.selectedUnlinkNames);
      for (var i = 0; i < keys.length; i++) {
        if (this.selectedUnlinkNames[keys[i]]) {
          count++;
        }
      }
      return count;
    },
    allLinkedSelected() {
      if (!this.linkedEmployers.length) {
        return false;
      }
      for (var i = 0; i < this.linkedEmployers.length; i++) {
        if (!this.selectedUnlinkNames[this.linkedEmployers[i].employer]) {
          return false;
        }
      }
      return true;
    },
  },
  watch: {
    visible: function (val) {
      if (val) {
        this.openModal();
      }
    },
    activeTab: function (val) {
      if (val === "linked") {
        this.fetchLinkedEmployers();
      }
    },
  },
  mounted() {
    if (this.visible) {
      this.openModal();
    }
  },
  methods: {
    openModal() {
      this.step = "select";
      this.maximized = false;
      this.loadError = null;
      this.searchInput = "";
      this.currentPage = 1;
      this.selectedNames = {};
      this.selectedUnlinkNames = {};
      this.linkedCount = 0;
      this.successMessage = "";
      this.linkedEmployers = [];
      this.activeTab = "link";
      this.fetchLinkedEmployers();
      this.fetchEmployers(true);
    },
    handleClose() {
      if (this.isLinking || this.isUnlinking) {
        return;
      }
      if (this.step === "done") {
        this.$emit("linked");
      }
      this.$emit("close");
    },
    isSelected(name) {
      return !!this.selectedNames[name];
    },
    toggleSelection(name) {
      if (this.selectedNames[name]) {
        var next = Object.assign({}, this.selectedNames);
        delete next[name];
        this.selectedNames = next;
      } else {
        this.selectedNames = Object.assign({}, this.selectedNames, { [name]: true });
      }
    },
    toggleAllMatched() {
      var next = Object.assign({}, this.selectedNames);
      var selectAll = !this.allMatchedSelected;
      for (var i = 0; i < this.matchedEmployers.length; i++) {
        var name = this.matchedEmployers[i].name;
        if (selectAll) {
          next[name] = true;
        } else {
          delete next[name];
        }
      }
      this.selectedNames = next;
    },
    isUnlinkSelected(name) {
      return !!this.selectedUnlinkNames[name];
    },
    toggleUnlinkSelection(name) {
      if (this.selectedUnlinkNames[name]) {
        var nextUnlink = Object.assign({}, this.selectedUnlinkNames);
        delete nextUnlink[name];
        this.selectedUnlinkNames = nextUnlink;
      } else {
        this.selectedUnlinkNames = Object.assign({}, this.selectedUnlinkNames, { [name]: true });
      }
    },
    toggleAllLinked() {
      var nextLinked = Object.assign({}, this.selectedUnlinkNames);
      var selectAllLinked = !this.allLinkedSelected;
      for (var j = 0; j < this.linkedEmployers.length; j++) {
        var linkedName = this.linkedEmployers[j].employer;
        if (selectAllLinked) {
          nextLinked[linkedName] = true;
        } else {
          delete nextLinked[linkedName];
        }
      }
      this.selectedUnlinkNames = nextLinked;
    },
    getSelectedUnlinkNamesList() {
      var unlinkNames = [];
      var unlinkKeys = Object.keys(this.selectedUnlinkNames);
      for (var k = 0; k < unlinkKeys.length; k++) {
        if (this.selectedUnlinkNames[unlinkKeys[k]]) {
          unlinkNames.push(unlinkKeys[k]);
        }
      }
      return unlinkNames;
    },
    onSearchInput() {
      var self = this;
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(function () {
        self.currentPage = 1;
        self.fetchOthers();
      }, 350);
    },
    goToPage(page) {
      this.currentPage = page;
      this.fetchOthers();
    },
    getSelectedNamesList() {
      var names = [];
      var keys = Object.keys(this.selectedNames);
      for (var i = 0; i < keys.length; i++) {
        if (this.selectedNames[keys[i]]) {
          names.push(keys[i]);
        }
      }
      return names;
    },
    linkedShareWidth(count) {
      var value = Number(count) || 0;
      if (!this.totalLinkedStudents || value <= 0) {
        return "0%";
      }
      var percent = Math.round((value / this.totalLinkedStudents) * 100);
      if (percent < 8) {
        return "8%";
      }
      return percent + "%";
    },
    linkedShareLabel(count) {
      var value = Number(count) || 0;
      if (!this.totalLinkedStudents || value <= 0) {
        return "0%";
      }
      return Math.round((value / this.totalLinkedStudents) * 100) + "%";
    },
    updateModalTabBadges() {
      var count = this.linkedEmployers.length;
      for (var i = 0; i < this.modalTabs.length; i++) {
        if (this.modalTabs[i].value === "linked") {
          this.modalTabs[i].badge = count > 0 ? String(count) : "";
        }
      }
    },
    applyMatchedSelections() {
      var next = Object.assign({}, this.selectedNames);
      for (var i = 0; i < this.matchedEmployers.length; i++) {
        next[this.matchedEmployers[i].name] = true;
      }
      this.selectedNames = next;
    },
    fireModalSwal(options) {
      if (!this.$swal) {
        return Promise.resolve(null);
      }
      var customClass = options.customClass || {};
      var merged = Object.assign({}, options, {
        customClass: Object.assign({}, customClass, {
          container: "cp-link-swal-container",
        }),
      });
      return this.$swal.fire(merged);
    },
    showSuccessToast(message) {
      if (!this.$swal) {
        return;
      }
      var Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true,
        customClass: {
          container: "cp-link-swal-container",
        },
      });
      Toast.fire({
        icon: "success",
        title: message || "Success",
      });
    },
    async fetchLinkedEmployers() {
      try {
        var result = await this.$apollo.query({
          query: gql`
            query GetLinkedEmployersForPartner($token: String!) {
              getLinkedEmployersForPartner(token: $token) {
                error
                message
                data {
                  total_employers
                  total_students
                  employers {
                    employer
                    count
                  }
                }
              }
            }
          `,
          variables: { token: this.partnerToken },
          fetchPolicy: "network-only",
        });

        var payload =
          result.data && result.data.getLinkedEmployersForPartner
            ? result.data.getLinkedEmployersForPartner
            : null;

        if (!payload || payload.error) {
          return;
        }

        var data = payload.data || {};
        this.linkedEmployers = data.employers || [];
        this.updateModalTabBadges();
      } catch (err) {
        this.linkedEmployers = [];
        this.updateModalTabBadges();
      }
    },
    async fetchEmployers(initialLoad) {
      this.isLoading = !!initialLoad;
      this.loadError = null;

      try {
        var result = await this.$apollo.query({
          query: gql`
            query GetUnlinkedEmployersForPartner(
              $token: String!
              $search: String
              $page: Int
              $first: Int
            ) {
              getUnlinkedEmployersForPartner(
                token: $token
                search: $search
                page: $page
                first: $first
              ) {
                error
                message
                data {
                  matched {
                    name
                    student_count
                  }
                  others {
                    data {
                      name
                      student_count
                    }
                    paginatorInfo {
                      count
                      currentPage
                      firstItem
                      hasMorePages
                      lastItem
                      lastPage
                      perPage
                      total
                    }
                  }
                }
              }
            }
          `,
          variables: {
            token: this.partnerToken,
            search: this.searchInput || null,
            page: this.currentPage,
            first: this.perPage,
          },
          fetchPolicy: "network-only",
        });

        var payload =
          result.data && result.data.getUnlinkedEmployersForPartner
            ? result.data.getUnlinkedEmployersForPartner
            : null;

        if (!payload || payload.error) {
          this.loadError = (payload && payload.message) || "Failed to load employers.";
          return;
        }

        var data = payload.data || {};
        this.matchedEmployers = data.matched || [];
        this.otherEmployers = (data.others && data.others.data) || [];
        this.paginatorInfo = (data.others && data.others.paginatorInfo) || null;

        if (initialLoad) {
          this.applyMatchedSelections();
        }
      } catch (err) {
        this.loadError = (err && err.message) ? err.message : "Failed to load employers.";
      } finally {
        this.isLoading = false;
        this.othersLoading = false;
      }
    },
    async fetchOthers() {
      this.othersLoading = true;
      await this.fetchEmployers(false);
    },
    async confirmLink() {
      var names = this.getSelectedNamesList();
      if (!names.length) {
        return;
      }

      this.isLinking = true;

      try {
        var result = await this.$apollo.mutate({
          mutation: gql`
            mutation LinkEmployersToCurrentPartner($token: String!, $employerNames: [String!]!) {
              linkEmployersToCurrentPartner(token: $token, employer_names: $employerNames) {
                error
                message
              }
            }
          `,
          variables: {
            token: this.partnerToken,
            employerNames: names,
          },
        });

        var payload =
          result.data && result.data.linkEmployersToCurrentPartner
            ? result.data.linkEmployersToCurrentPartner
            : null;

        if (!payload || payload.error) {
          this.fireModalSwal({
            icon: "error",
            title: "Link failed",
            text: (payload && payload.message) || "Unable to link employers.",
          });
          return;
        }

        this.linkedCount = names.length;
        this.successMessage = payload.message || "Employers linked successfully.";
        this.fetchLinkedEmployers();
        this.step = "done";
      } catch (err) {
        this.fireModalSwal({
          icon: "error",
          title: "Link failed",
          text: (err && err.message) ? err.message : "Unable to link employers.",
        });
      } finally {
        this.isLinking = false;
      }
    },
    async confirmUnlink() {
      var unlinkNames = this.getSelectedUnlinkNamesList();
      if (!unlinkNames.length) {
        return;
      }

      var confirmResult = await this.fireModalSwal({
        icon: "warning",
        title: "Unlink employers?",
        text: "This will remove the partner link from " + unlinkNames.length + " employer name(s) and related student records.",
        showCancelButton: true,
        confirmButtonText: "Unlink",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#dc2626",
      });
      if (this.$swal && (!confirmResult || !confirmResult.isConfirmed)) {
        return;
      }

      this.isUnlinking = true;

      try {
        var result = await this.$apollo.mutate({
          mutation: gql`
            mutation UnlinkEmployersFromCurrentPartner($token: String!, $employerNames: [String!]!) {
              unlinkEmployersFromCurrentPartner(token: $token, employer_names: $employerNames) {
                error
                message
              }
            }
          `,
          variables: {
            token: this.partnerToken,
            employerNames: unlinkNames,
          },
        });

        var payload =
          result.data && result.data.unlinkEmployersFromCurrentPartner
            ? result.data.unlinkEmployersFromCurrentPartner
            : null;

        if (!payload || payload.error) {
          this.fireModalSwal({
            icon: "error",
            title: "Unlink failed",
            text: (payload && payload.message) || "Unable to unlink employers.",
          });
          return;
        }

        this.selectedUnlinkNames = {};
        await this.fetchLinkedEmployers();
        await this.fetchEmployers(true);
        this.$emit("linked");

        this.showSuccessToast(payload.message || "Employers unlinked successfully.");
      } catch (err) {
        this.fireModalSwal({
          icon: "error",
          title: "Unlink failed",
          text: (err && err.message) ? err.message : "Unable to unlink employers.",
        });
      } finally {
        this.isUnlinking = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.cp-link-fade-enter-active,
.cp-link-fade-leave-active {
  transition: opacity 0.2s ease;
}

.cp-link-fade-enter-from,
.cp-link-fade-leave-to {
  opacity: 0;
}

:deep(.base-modal-control-btn.cp-link-modal-expand-btn:hover),
:deep(.base-modal-control-btn.cp-link-modal-expand-btn:focus-visible) {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.35);
}

.cp-link-modal-header {
  padding: 1.25rem 3rem 1.25rem 1.5rem;
  background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 45%, #3b82f6 100%);
  color: #fff;
  border-radius: 1rem 1rem 0 0;
}

.cp-link-modal-header--fullscreen {
  border-radius: 0;
}

.cp-link-modal-header-inner {
  display: flex;
  align-items: center;
  gap: 0.875rem;
}

.cp-link-modal-header-icon-wrap {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cp-link-modal-header-icon {
  font-size: 1.35rem;
}

.cp-link-modal-header-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
}

.cp-link-modal-header-sub {
  margin: 0.2rem 0 0;
  font-size: 0.8125rem;
  color: rgba(219, 234, 254, 0.95);
}

.cp-link-modal-shell {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.cp-link-modal-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 1.5rem;
  background: linear-gradient(180deg, #f8fafc 0%, #eff6ff 100%);
}

.cp-link-modal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  min-height: 12rem;
  color: #64748b;
}

.cp-link-modal-loading--compact {
  min-height: 6rem;
}

.cp-link-tabs {
  display: flex;
  gap: 0.5rem;
  padding: 0.375rem;
  margin-bottom: 1rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.85rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.cp-link-tab {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-width: 0;
  padding: 0.7rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #64748b;
  background: transparent;
  border: none;
  border-radius: 0.625rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.cp-link-tab:hover {
  background: #f8fafc;
  color: #334155;
}

.cp-link-tab--active {
  color: #fff;
  background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%);
  box-shadow: 0 8px 18px -12px rgba(37, 99, 235, 0.9);
}

.cp-link-tab--active:hover {
  color: #fff;
  background: linear-gradient(180deg, #1d4ed8 0%, #1e40af 100%);
}

.cp-link-tab__icon {
  font-size: 0.875rem;
  flex-shrink: 0;
}

.cp-link-tab__label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cp-link-tab__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.35rem;
  font-size: 0.6875rem;
  font-weight: 700;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.22);
  color: inherit;
}

.cp-link-tab:not(.cp-link-tab--active) .cp-link-tab__badge {
  background: #dbeafe;
  color: #1d4ed8;
}

.cp-link-tab-panel {
  animation: cp-link-tab-fade 0.2s ease;
}

@keyframes cp-link-tab-fade {
  from {
    opacity: 0;
    transform: translateY(4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.cp-link-linked-hero {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.125rem;
  margin-bottom: 1rem;
  border-radius: 0.9rem;
  border: 1px solid #bbf7d0;
  background: linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 45%, #ffffff 100%);
}

.cp-link-linked-hero__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #065f46;
}

.cp-link-linked-hero__sub {
  margin: 0.35rem 0 0;
  font-size: 0.8125rem;
  color: #047857;
  line-height: 1.45;
}

.cp-link-linked-hero__stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.cp-link-linked-stat {
  padding: 0.85rem 1rem;
  border-radius: 0.75rem;
  border: 1px solid #bbf7d0;
  background: rgba(255, 255, 255, 0.9);
  text-align: center;
}

.cp-link-linked-stat__value {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 800;
  color: #065f46;
  line-height: 1.1;
}

.cp-link-linked-stat__label {
  margin: 0.2rem 0 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #047857;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.cp-link-linked-table-wrap {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.9rem;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.cp-link-linked-table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
}

.cp-link-linked-table-toolbar__hint {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #b45309;
}

.cp-link-linked-table-head {
  display: grid;
  grid-template-columns: 2.5rem minmax(0, 1fr) 5.5rem 4.5rem;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.cp-link-linked-table-head__col {
  font-size: 0.6875rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.cp-link-linked-table-head__col--count,
.cp-link-linked-table-head__col--share {
  text-align: right;
}

.cp-link-linked-row {
  display: grid;
  grid-template-columns: 2.5rem minmax(0, 1fr) 5.5rem 4.5rem;
  gap: 0.75rem;
  align-items: center;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.cp-link-linked-row:hover {
  background: #f8fafc;
}

.cp-link-linked-row--selected {
  background: #fff7ed;
  border-bottom-color: #fed7aa;
}

.cp-link-linked-row__check {
  display: flex;
  align-items: center;
  justify-content: center;
}

.cp-link-linked-row:last-child {
  border-bottom: none;
}

.cp-link-linked-row__main {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  min-width: 0;
}

.cp-link-linked-row__rank {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  flex-shrink: 0;
  border-radius: 9999px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 0.6875rem;
  font-weight: 700;
}

.cp-link-linked-row__info {
  min-width: 0;
  flex: 1;
}

.cp-link-linked-row__name {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.35;
  word-break: break-word;
}

.cp-link-linked-row__bar-wrap {
  margin-top: 0.45rem;
  height: 0.35rem;
  border-radius: 9999px;
  background: #e2e8f0;
  overflow: hidden;
}

.cp-link-linked-row__bar {
  height: 100%;
  border-radius: 9999px;
  background: linear-gradient(90deg, #34d399 0%, #059669 100%);
}

.cp-link-linked-row__count,
.cp-link-linked-row__share {
  text-align: right;
  font-size: 0.875rem;
  font-weight: 700;
  color: #0f172a;
}

.cp-link-linked-row__share {
  color: #64748b;
  font-weight: 600;
}

.cp-link-linked-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 16rem;
  padding: 2rem 1.25rem;
  border: 1px dashed #cbd5e1;
  border-radius: 0.9rem;
  background: #fff;
}

.cp-link-linked-empty__icon-wrap {
  width: 3.5rem;
  height: 3.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 1rem;
  background: #eff6ff;
  color: #2563eb;
  font-size: 1.25rem;
  margin-bottom: 0.85rem;
}

.cp-link-linked-empty__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
}

.cp-link-linked-empty__text {
  margin: 0.5rem 0 0;
  max-width: 22rem;
  font-size: 0.8125rem;
  color: #64748b;
  line-height: 1.5;
}

.cp-link-linked-empty__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.25rem;
  padding: 0.625rem 1rem;
  border: none;
  border-radius: 0.625rem;
  background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%);
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
}

.cp-link-linked-empty__btn:hover {
  box-shadow: 0 8px 18px -12px rgba(37, 99, 235, 0.9);
}

.cp-link-modal-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.9rem;
  padding: 1rem 1.125rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.cp-link-modal-card--error {
  border-color: #fecaca;
  background: #fef2f2;
}

.cp-link-modal-card__head {
  margin-bottom: 0.85rem;
}

.cp-link-modal-card__head--row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.cp-link-modal-card__title {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #0f172a;
}

.cp-link-modal-card__title--error {
  color: #b91c1c;
}

.cp-link-modal-card__sub {
  margin: 0.25rem 0 0;
  font-size: 0.8125rem;
  color: #64748b;
  line-height: 1.4;
}

.cp-link-modal-text-btn {
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.cp-link-modal-ready {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  background: rgba(37, 99, 235, 0.08);
  border: 1px solid rgba(37, 99, 235, 0.2);
  border-radius: 0.9rem;
  padding: 0.9rem 1rem;
  margin-bottom: 1rem;
}

.cp-link-modal-ready__icon {
  margin-top: 0.1rem;
  color: #2563eb;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.cp-link-modal-ready__title {
  margin: 0;
  font-weight: 700;
  color: #1e40af;
}

.cp-link-modal-ready__sub {
  margin: 0.35rem 0 0;
  color: #475569;
  font-size: 0.8125rem;
}

.cp-link-search-wrap {
  position: relative;
  margin-bottom: 0.85rem;
}

.cp-link-search-wrap__icon {
  position: absolute;
  left: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 0.875rem;
}

.cp-link-search-wrap__input {
  width: 100%;
  padding: 0.625rem 0.875rem 0.625rem 2.25rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  color: #0f172a;
  background: #fff;
}

.cp-link-search-wrap__input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.cp-link-employer-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.cp-link-employer-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  background: #f8fafc;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.cp-link-employer-item:hover {
  border-color: #bfdbfe;
  background: #eff6ff;
}

.cp-link-employer-item--matched {
  border-color: #bfdbfe;
  background: linear-gradient(180deg, #eff6ff 0%, #f8fafc 100%);
}

.cp-link-employer-item__checkbox {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

.cp-link-employer-item__body {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.cp-link-employer-item__name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.35;
  word-break: break-word;
}

.cp-link-employer-item__meta {
  font-size: 0.75rem;
  color: #64748b;
}

.cp-link-employer-item__badge {
  flex-shrink: 0;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.cp-link-empty-inline {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 0.5rem;
  min-height: 8rem;
  color: #94a3b8;
  font-size: 0.8125rem;
  border: 1px dashed #cbd5e1;
  border-radius: 0.75rem;
  background: #f8fafc;
  padding: 1rem;
}

.cp-link-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 0.85rem;
}

.cp-link-pagination__btn {
  padding: 0.375rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.5rem;
  background: #fff;
  color: #334155;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
}

.cp-link-pagination__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cp-link-pagination__label {
  font-size: 0.8125rem;
  color: #64748b;
}

.cp-link-modal-hint {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 0.8125rem;
  color: #64748b;
}

.cp-link-modal-hint-icon {
  color: #f59e0b;
}

.cp-link-modal-summary {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.cp-link-modal-summary__stat {
  border-radius: 0.85rem;
  padding: 1rem;
  text-align: center;
  background: #fff;
  border: 1px solid #e2e8f0;
}

.cp-link-modal-summary__stat--ok {
  background: #ecfdf5;
  border-color: #a7f3d0;
}

.cp-link-modal-summary__value {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 800;
  color: #047857;
}

.cp-link-modal-summary__label {
  margin: 0.25rem 0 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #065f46;
}

.cp-link-modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  background: #fff;
}

.cp-link-modal-footer--fullscreen {
  border-radius: 0;
}

.cp-link-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.625rem;
}

.cp-link-modal-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
}

.cp-link-modal-btn-secondary {
  background: #fff;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.cp-link-modal-btn-primary {
  background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%);
  color: #fff;
}

.cp-link-modal-btn-danger {
  background: linear-gradient(180deg, #dc2626 0%, #b91c1c 100%);
  color: #fff;
}

.cp-link-modal-btn-primary:disabled,
.cp-link-modal-btn-secondary:disabled,
.cp-link-modal-btn-danger:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>

<style lang="scss">
.cp-link-swal-container.swal2-container {
  z-index: 10100 !important;
}
</style>
